import { useState } from 'react'

import { DATA_FACING_KEY } from '@components/shelfDetails/Facing'

import type { PointerEvent } from 'react'

export const ALL_VIEW_TYPES = ['product', 'productLegacy', 'schematic', 'image'] as const

export type ViewType = typeof ALL_VIEW_TYPES[number]

/**
 * Represents the hover state within the shelf container:
 * - `false`: mouse is outside the container
 * - `true`: mouse is in the container but not on a facing
 * - `string`: mouse is hovering a specific facing (the facing id)
 */
type HoverState = boolean | string

/**
 * Custom hook to manage hover state within a shelf container.
 * Uses pointer tracking at container level.
 */
export function useShelfHoverState() {
  const [hoverState, setHoverState] = useState<HoverState>(false)

  /**
   * Handler for pointer movement within the container.
   * Detects which facing (if any) is under the pointer using DOM traversal.
   */
  function handlePointerMove(e: PointerEvent) {
    const target = e.target as HTMLElement
    const facingElement = target.closest(`[${DATA_FACING_KEY}]`)

    if (facingElement) {
      const facingId = facingElement.getAttribute(DATA_FACING_KEY)

      if (facingId) {
        setHoverState(facingId)
        return
      }
    }

    // Pointer is in container but not on a facing
    setHoverState(true)
  }

  /**
   * Handler for pointer leaving the container entirely.
   */
  const handlePointerLeave = () => {
    setHoverState(false)
  }

  return {
    hoverState,
    /** Attach to container's onPointerMove */
    onPointerMove: handlePointerMove,
    /** Attach to container's onPointerLeave */
    onPointerLeave: handlePointerLeave,
  }
}

